import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { runnerApi, type Me, type PickupRun } from './runnerApiClient';
import { onRunnerMutation } from './mutationEvents';
export function useRunnerNotices(me: Me | null) {
  const { pathname } = useLocation();
  const owner = me?.runnerId ?? '';
  const canRead = Boolean(owner && me?.capabilities.includes('runner:runs:read'));
  const [notice, setNotice] = useState<{ owner: string; activeRun: PickupRun | null; disputeCount: number | null }>({ owner: '', activeRun: null, disputeCount: null });
  useEffect(() => {
    let cancelled = false;
    if (!canRead) return;
    let refreshing = false;
    let pending = false;
    const refresh = async () => {
      if (cancelled) return;
      if (refreshing) { pending = true; return; }
      refreshing = true;
      try {
        let page = await runnerApi.runs({ view: 'active', limit: 100 });
        let offset = 0; let ongoing: PickupRun | undefined;
        while (!cancelled) {
          ongoing = [...page.runs].sort((a, b) => b.runDate.localeCompare(a.runDate) || a.deliveryWindowStart.localeCompare(b.deliveryWindowStart) || a.id.localeCompare(b.id))
            .find(run => ['in_prep', 'pickup_in_progress', 'out_for_delivery'].includes(run.status));
          if (ongoing || !page.hasMore) break;
          offset += page.runs.length;
          if (!page.runs.length || offset >= 10000) throw new Error('Incomplete active run list');
          page = await runnerApi.runs({ view: 'active', limit: 100, offset });
        }
        if (!cancelled) setNotice(old => ({ owner, activeRun: ongoing ?? null, disputeCount: old.owner === owner ? old.disputeCount : null }));
      } catch { /* Preserve the last known notice when temporarily disconnected. */ }
      try { const result = await runnerApi.orderAttention('disputes'); if (!cancelled) setNotice(old => ({ owner, activeRun: old.owner === owner ? old.activeRun : null, disputeCount: result.total })); }
      catch { /* Unknown is not a confident zero. */ }
      finally { refreshing = false; if (pending && !cancelled) { pending = false; void refresh(); } }
    };
    void refresh();
    const unsubscribe = onRunnerMutation(() => { void refresh(); });
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, 45000);
    return () => { cancelled = true; unsubscribe(); window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [owner, canRead, me?.lifecycle, pathname]);
  return canRead && notice.owner === owner ? notice : { activeRun: null, disputeCount: null };
}
